/**
 * Mordeczka.eu Admin - Dashboard Page with Ant Design
 */

import {
  Card,
  Col,
  Row,
  Statistic,
  Typography,
  Button,
  Space,
  List,
  Tag,
  Divider,
} from "antd";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  BookOutlined,
  VideoCameraOutlined,
  GlobalOutlined,
  ClusterOutlined,
  UserOutlined,
  IdcardOutlined,
  BankOutlined,
  VideoCameraAddOutlined,
  CloudServerOutlined,
  EditOutlined,
  TranslationOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { Tv } from "lucide-react";
import {
  worksApi,
  languagesApi,
  tagsApi,
  genresApi,
  literaryGenresApi,
  authorsApi,
  publishersApi,
  directorsApi,
  streamingPlatformsApi,
  nationalitiesApi,
  rolesApi,
  imagesApi,
  auditLogsApi,
} from "../services/api";
import { usePermissions } from "../hooks/usePermissions";

const { Title, Text } = Typography;

export function DashboardPage() {
  const navigate = useNavigate();
  const { canCreate } = usePermissions();

  // Fetch works by type
  const { data: books } = useQuery({
    queryKey: ["works", "book"],
    queryFn: () => worksApi.list({ page: 1, page_size: 1, type: "book" }),
  });

  const { data: movies } = useQuery({
    queryKey: ["works", "movie"],
    queryFn: () => worksApi.list({ page: 1, page_size: 1, type: "movie" }),
  });

  const { data: series } = useQuery({
    queryKey: ["works", "series"],
    queryFn: () => worksApi.list({ page: 1, page_size: 1, type: "series" }),
  });

  // Fetch the latest added work
  const { data: latestWork, isLoading: latestWorkLoading } = useQuery({
    queryKey: ["works", "latest"],
    queryFn: () =>
      worksApi.list({
        page: 1,
        page_size: 1,
        sort: "created_at",
        order: "desc",
      }),
  });

  // Fetch dictionaries
  const { data: languages } = useQuery({
    queryKey: ["languages"],
    queryFn: () => languagesApi.list({ page: 1, page_size: 1 }),
  });

  const { data: tags } = useQuery({
    queryKey: ["tags"],
    queryFn: () => tagsApi.list({ page: 1, page_size: 1 }),
  });

  const { data: genres } = useQuery({
    queryKey: ["genres"],
    queryFn: () => genresApi.list({ page: 1, page_size: 1 }),
  });

  const { data: literaryGenres } = useQuery({
    queryKey: ["literary-genres"],
    queryFn: () => literaryGenresApi.list({ page: 1, page_size: 1 }),
  });

  const { data: authors } = useQuery({
    queryKey: ["authors"],
    queryFn: () => authorsApi.list({ page: 1, page_size: 1 }),
  });

  const { data: publishers } = useQuery({
    queryKey: ["publishers"],
    queryFn: () => publishersApi.list({ page: 1, page_size: 1 }),
  });

  const { data: directors } = useQuery({
    queryKey: ["directors"],
    queryFn: () => directorsApi.list({ page: 1, page_size: 1 }),
  });

  const { data: streamingPlatforms } = useQuery({
    queryKey: ["streaming-platforms"],
    queryFn: () => streamingPlatformsApi.list({ page: 1, page_size: 1 }),
  });

  const { data: nationalities } = useQuery({
    queryKey: ["nationalities"],
    queryFn: () => nationalitiesApi.list({ page: 1, page_size: 1 }),
  });

  const { data: roles } = useQuery({
    queryKey: ["roles"],
    queryFn: () => rolesApi.list({ page: 1, page_size: 1 }),
  });

  const { data: auditLogs, isLoading: logsLoading } = useQuery({
    queryKey: ["audit-logs-recent"],
    queryFn: () => auditLogsApi.list({ limit: 10 }),
  });

  const worksStats = [
    {
      title: "Książki",
      value: books?.total || 0,
      icon: <BookOutlined />,
      color: "#1677ff",
      path: "/books",
    },
    {
      title: "Filmy",
      value: movies?.total || 0,
      icon: <VideoCameraOutlined />,
      color: "#52c41a",
      path: "/movies",
    },
    {
      title: "Seriale",
      value: series?.total || 0,
      icon: <Tv size={24} />,
      color: "#faad14",
      path: "/series",
    },
  ];

  const dictionaryStats = [
    {
      title: "Pisarze",
      value: authors?.total || 0,
      path: "/authors",
      icon: <UserOutlined />,
      color: "#1677ff",
    },
    {
      title: "Gatunki filmowe",
      value: genres?.total || 0,
      path: "/genres",
      icon: <ClusterOutlined />,
      color: "#eb2f96",
    },
    {
      title: "Gatunki lit.",
      value: literaryGenres?.total || 0,
      path: "/literary-genres",
      icon: <EditOutlined />,
      color: "#722ed1",
    },
    {
      title: "Języki",
      value: languages?.total || 0,
      path: "/languages",
      icon: <TranslationOutlined />,
      color: "#52c41a",
    },
    {
      title: "Kraje",
      value: nationalities?.total || 0,
      path: "/nationalities",
      icon: <GlobalOutlined />,
      color: "#13c2c2",
    },
    {
      title: "Platformy",
      value: streamingPlatforms?.total || 0,
      path: "/streaming-platforms",
      icon: <CloudServerOutlined />,
      color: "#52c41a",
    },
    {
      title: "Reżyserzy",
      value: directors?.total || 0,
      path: "/directors",
      icon: <VideoCameraAddOutlined />,
      color: "#fa8c16",
    },
    {
      title: "Role",
      value: roles?.total || 0,
      path: "/roles",
      icon: <IdcardOutlined />,
      color: "#722ed1",
    },
    {
      title: "Tagi",
      value: tags?.total || 0,
      path: "/tags",
      icon: <TagOutlined />,
      color: "#faad14",
    },
    {
      title: "Wydawnictwa",
      value: publishers?.total || 0,
      path: "/publishers",
      icon: <BankOutlined />,
      color: "#13c2c2",
    },
  ];

  const quickActions = [
    { title: "Dodaj książkę", path: "/books/new", icon: <BookOutlined /> },
    { title: "Dodaj film", path: "/movies/new", icon: <VideoCameraOutlined /> },
    { title: "Dodaj serial", path: "/series/new", icon: <Tv size={14} /> },
  ];

  return (
    <div>
      <Title level={2} style={{ marginBottom: 24 }}>
        Panel główny
      </Title>

      {/* Dzieła - główna sekcja */}
      <Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
        Dzieła
      </Title>
      <Row gutter={[16, 16]}>
        {worksStats.map((stat) => (
          <Col xs={24} sm={8} key={stat.title}>
            <Card
              hoverable
              onClick={() => navigate(stat.path)}
              style={{ cursor: "pointer" }}
            >
              <Statistic
                title={stat.title}
                value={stat.value}
                prefix={
                  <span style={{ color: stat.color, fontSize: "24px" }}>
                    {stat.icon}
                  </span>
                }
                valueStyle={{ fontSize: "32px" }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Słowniki - mniejsze karty */}
      <Title level={4} style={{ marginTop: 32, marginBottom: 16 }}>
        Słowniki
      </Title>
      <Row gutter={[12, 12]}>
        {dictionaryStats.map((stat) => (
          <Col
            xs={12}
            sm={8}
            md={6}
            lg={4}
            xl={4}
            className="col-xl-5"
            key={stat.title}
          >
            <Card
              hoverable
              size="small"
              onClick={() => navigate(stat.path)}
              style={{ cursor: "pointer", textAlign: "center" }}
            >
              <Statistic
                title={<span style={{ fontSize: "12px" }}>{stat.title}</span>}
                value={stat.value}
                prefix={
                  <span style={{ color: stat.color, fontSize: "16px" }}>
                    {stat.icon}
                  </span>
                }
                valueStyle={{ fontSize: "20px" }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        {canCreate && (
          <Col xs={24} lg={12}>
            <Card title="Szybkie akcje">
              <Space direction="vertical" style={{ width: "100%" }}>
                {quickActions.map((action) => (
                  <Button
                    key={action.title}
                    type="dashed"
                    icon={action.icon}
                    onClick={() => navigate(action.path)}
                    block
                    style={{ textAlign: "left" }}
                  >
                    {action.title}
                  </Button>
                ))}
              </Space>
            </Card>
          </Col>
        )}
        <Col xs={24} lg={canCreate ? 12 : 24}>
          <Card title="Ostatnio dodane dzieło" loading={latestWorkLoading}>
            {latestWork && latestWork.items.length > 0 ? (
              <div
                style={{ cursor: "pointer", display: "flex", gap: 16 }}
                onClick={() => {
                  const work = latestWork.items[0];
                  const path =
                    work.type === "book"
                      ? "/books"
                      : work.type === "movie"
                        ? "/movies"
                        : "/series";
                  // Navigate to the list or we could navigate to detail if we had it, but for now books/movies/series list
                  navigate(path);
                }}
              >
                {latestWork.items[0].cover_image_id && (
                  <img
                    src={imagesApi.getFileUrl(
                      latestWork.items[0].cover_image_id,
                    )}
                    alt={latestWork.items[0].title}
                    style={{
                      width: 60,
                      height: 90,
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: "bold",
                      fontSize: "16px",
                      marginBottom: 4,
                    }}
                  >
                    {latestWork.items[0].title}
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    <Text type="secondary">
                      {latestWork.items[0].type === "book"
                        ? "Książka"
                        : latestWork.items[0].type === "movie"
                          ? "Film"
                          : "Serial"}
                      {latestWork.items[0].release_year
                        ? ` (${latestWork.items[0].release_year})`
                        : ""}
                    </Text>
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      Dodano:{" "}
                      {new Date(latestWork.items[0].created_at).toLocaleString(
                        "pl-PL",
                      )}
                    </Text>
                  </div>
                </div>
              </div>
            ) : (
              <Text type="secondary">Brak dodanych dzieł</Text>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card
            title="Ostatnia aktywność"
            loading={logsLoading}
            styles={{ body: { padding: 0 } }}
          >
            <List
              dataSource={auditLogs || []}
              renderItem={(log) => (
                <List.Item style={{ padding: "12px 24px" }}>
                  <List.Item.Meta
                    title={
                      <Space>
                        <Tag
                          color={
                            log.action === "create"
                              ? "green"
                              : log.action === "update"
                                ? "blue"
                                : "red"
                          }
                        >
                          {log.action === "create"
                            ? "Dodano"
                            : log.action === "update"
                              ? "Zaktualizowano"
                              : "Usunięto"}
                        </Tag>
                        <span style={{ fontWeight: 500 }}>
                          {log.entity_name || log.entity_type}
                        </span>
                      </Space>
                    }
                    description={
                      <Space split={<Divider type="vertical" />}>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          {log.entity_type === "work"
                            ? "Dzieło"
                            : log.entity_type}
                        </Text>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          {new Date(log.created_at).toLocaleString("pl-PL")}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
              locale={{
                emptyText: (
                  <div style={{ padding: 24 }}>
                    <Text type="secondary">Brak ostatniej aktywności</Text>
                  </div>
                ),
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default DashboardPage;
